<template>
    <form data-test-id="form-register" class="form-register align-middle" @submit.prevent="register">
        <div class="register-card">
            <header class="register-header">
                <div>
                    <img class="register-logo" width="200" src="/images/logo-lead.png" :alt="themeBrandName" />
                    <h5 data-test-id="title-register_page" class="register-title">
                        {{ $tl("labels.form.register.title") }}
                    </h5>
                    <p v-if="isNoShow" class="register-intro">
                        {{ $t("infos.register.welcome1") }}<br />
                        {{ $t("infos.register.welcome2") }}
                    </p>
                </div>
                <ChooseLang ref="chooseLang" class="register-lang" @lang-changed="user.lang = $event" />
            </header>

            <loader-component v-if="loading" class="register-loader" />

            <div v-if="errorMessage" class="alert-danger alert register-alert">
                {{ errorMessage }}
            </div>

            <section class="register-section">
                <div class="register-section-title">{{ $tl("labels.form.register.personalSection") }}</div>
                <div class="register-grid">
                    <div class="register-field">
                        <label for="firstname" class="form-label register-label"> {{ $tl("labels.form.firstname") }} <small>*</small> </label>
                        <input
                            id="firstname"
                            type="text"
                            data-test-id="input-txt-firstname"
                            class="form-control"
                            :class="{ 'is-invalid': hasErrors('firstname') }"
                            name="firstname"
                            v-model="user.firstname"
                            @input="clearErrors('firstname')"
                            autocomplete="given-name"
                            required
                            autofocus />
                        <div v-if="hasErrors('firstname')" class="invalid-feedback" role="alert" v-html="joinErrors('firstname')"></div>
                    </div>

                    <div class="register-field">
                        <label for="lastname" class="form-label register-label"> {{ $tl("labels.form.lastname") }} <small>*</small> </label>
                        <input
                            id="lastname"
                            type="text"
                            data-test-id="input-txt-lastname"
                            class="form-control"
                            :class="{ 'is-invalid': hasErrors('lastname') }"
                            v-model="user.lastname"
                            @input="clearErrors('lastname')"
                            name="lastname"
                            autocomplete="family-name"
                            required />
                        <div v-if="hasErrors('lastname')" class="invalid-feedback" role="alert" v-html="joinErrors('lastname')"></div>
                    </div>
                </div>
            </section>

            <section class="register-section">
                <div class="register-section-title">{{ $tl("labels.form.register.contactSection") }}</div>
                <div class="register-grid">
                    <div class="register-field">
                        <label for="email" class="form-label register-label"> {{ $tl("labels.form.emailBusiness") }} <small>*</small> </label>
                        <input
                            id="email"
                            type="email"
                            data-test-id="input-txt-email"
                            class="form-control"
                            :class="{ 'is-invalid': hasErrors('email') }"
                            v-model="user.email"
                            @input="clearErrors('email')"
                            name="email"
                            autocomplete="email"
                            required />
                        <div
                            data-test-id="txt-error-email"
                            v-if="hasErrors('email')"
                            class="invalid-feedback"
                            role="alert"
                            v-html="joinErrors('email')"></div>
                    </div>

                    <div class="register-field">
                        <label for="tel" class="form-label register-label"> {{ $tl("labels.form.portalPhoneNumber") }} <small>*</small> </label>
                        <input
                            id="tel"
                            type="tel"
                            data-test-id="input-txt-tel"
                            class="form-control"
                            :class="{ 'is-invalid': hasErrors('tel') }"
                            name="tel"
                            v-model="user.tel"
                            @input="clearErrors('tel')"
                            autocomplete="tel"
                            required />
                        <div v-if="hasErrors('tel')" class="invalid-feedback" role="alert" v-html="joinErrors('tel')"></div>
                    </div>
                </div>
            </section>

            <section class="register-section register-section-password">
                <div class="register-section-title">{{ $tl("labels.form.register.securitySection") }}</div>
                <div class="register-grid">
                    <div class="register-field">
                        <label for="password" class="form-label register-label"> {{ $tl("labels.form.password") }} <small>*</small> </label>
                        <PasswordInputComponent id="password" v-model="user.password" dataTestId="input-txt-password" @input="clearPasswordErrors" />
                        <div v-if="hasErrors('password')" class="invalid-feedback d-block" role="alert" v-html="joinErrors('password')"></div>
                    </div>

                    <div class="register-field">
                        <label for="password-confirm" class="form-label register-label">
                            {{ $tl("labels.form.passwordConfirm") }} <small>*</small>
                        </label>
                        <PasswordConfirmationInputComponent
                            id="password-confirm"
                            dataTestId="input-txt-password_confirm"
                            :mainPassword="user.password"
                            v-model="user.password_confirmation"
                            @input="clearPasswordConfirmationErrors" />
                        <div
                            v-if="hasAnyErrors(['password_confirmation', 'password-confirm'])"
                            class="invalid-feedback d-block"
                            role="alert"
                            v-html="joinErrorsFor(['password_confirmation', 'password-confirm'])"></div>
                    </div>
                </div>
            </section>

            <section class="register-section register-legal-section">
                <label class="form-label register-checkbox" :class="{ 'is-invalid': hasErrors('accept_cgv') }" for="accept_cgv">
                    <input
                        type="checkbox"
                        id="accept_cgv"
                        data-test-id="input-chk-accept_cgv"
                        name="accept_cgv"
                        v-model="user.accept_cgv"
                        required
                        @change="clearErrors('accept_cgv')" />
                    <span class="checkmark"></span>
                    <span>
                        {{ $t("labels.form.register.CGU1") }}
                        <a class="fw-bold" :href="`/cgu_global.pdf?v=${$__BUILD_ID__}`" target="_blank">{{ $t("labels.form.register.CGU2") }}</a>
                        <small>*</small>
                    </span>
                    <div v-if="hasErrors('accept_cgv')" class="invalid-feedback d-block" role="alert" v-html="joinErrors('accept_cgv')"></div>
                </label>

                <label
                    class="form-label register-checkbox"
                    :class="{ 'is-invalid': hasErrors('accept_data_processing') }"
                    for="accept_data_processing">
                    <input
                        type="checkbox"
                        id="accept_data_processing"
                        data-test-id="input-chk-accept_data_processing"
                        name="accept_data_processing"
                        v-model="user.accept_data_processing"
                        required
                        @change="clearErrors('accept_data_processing')" />
                    <span class="checkmark"></span>
                    <span>
                        {{ $t("labels.form.register.PCDP1") }}
                        <a class="fw-bold" :href="`/datapolicy.pdf?v=${$__BUILD_ID__}`" target="_blank">{{ $tl("labels.rgpd.pcdp") }}</a
                        >.
                        <small>*</small>
                    </span>
                    <div
                        v-if="hasErrors('accept_data_processing')"
                        class="invalid-feedback d-block"
                        role="alert"
                        v-html="joinErrors('accept_data_processing')"></div>
                </label>
            </section>

            <footer class="register-actions">
                <button data-test-id="btn-register" type="submit" class="btn btn-success btn-circle register-submit" :disabled="loading">
                    {{ $tl("labels.form.register.button") }}
                </button>
                <router-link data-test-id="lnk-login_page" class="register-login-link" :to="{ name: 'public.auth.login' }">
                    {{ $tl("labels.form.register.goToLoginPage") }}
                </router-link>
            </footer>
        </div>
    </form>
</template>

<script>
import LoaderComponent from "../../components/LoaderComponent.vue";
import PasswordInputComponent from "../../components/forms/PasswordInputComponent.vue";
import PasswordConfirmationInputComponent from "../../components/forms/PasswordConfirmationInputComponent.vue";
import ChooseLang from "../../components/Default/ChooseLang.vue";

export default {
    name: "AuthRegister",
    data() {
        return {
            loading: false,
            errorMessage: null,
            errors: null,
            user: {
                firstname: "",
                lastname: "",
                email: "",
                tel: "",
                lang: undefined,
                password: "",
                password_confirmation: "",
                accept_cgv: false,
                accept_data_processing: false,
            },
        };
    },
    computed: {},
    methods: {
        setUserLang(newLang) {
            this.setUserGlobalLang(newLang);
            this.user.lang = newLang;
        },
        register() {
            this.loading = true;
            this.errors = null;
            this.errorMessage = null;
            this.$store
                .dispatch("auth/register", {
                    user: {
                        ...this.user,
                        lang: this.userLang,
                    },
                })
                .then(() => {
                    window.location.href = "/";
                })
                .catch((error) => {
                    this.errorMessage = this.getErrorMsgFromErrorResponse(error);

                    if (error.response && error.response.data.errors) {
                        this.errors = error.response.data.errors;
                    }

                    this.user.password = "";
                    this.user.password_confirmation = "";
                    this.loading = false;
                });
        },
        clearPasswordErrors() {
            this.clearErrors("password");
            this.clearErrors("password_confirmation");
            this.clearErrors("password-confirm");
        },
        clearPasswordConfirmationErrors() {
            this.clearErrors("password_confirmation");
            this.clearErrors("password-confirm");
        },
        clearErrors(key) {
            if (!this.errors) return;

            if (this.errors[key]) {
                this.$delete(this.errors, key);
            }

            if (Object.keys(this.errors).length === 0) {
                this.errors = null;
                this.errorMessage = null;
            }
        },
        hasErrors(key) {
            return this.errors !== null && this.errors[key] && this.errors[key].length > 0;
        },
        hasAnyErrors(keys) {
            return keys.some((key) => this.hasErrors(key));
        },
        joinErrors(key) {
            return this.errors[key].join("<br/>");
        },
        joinErrorsFor(keys) {
            return keys
                .filter((key) => this.hasErrors(key))
                .flatMap((key) => this.errors[key])
                .join("<br/>");
        },
    },
    components: {
        LoaderComponent,
        PasswordInputComponent,
        PasswordConfirmationInputComponent,
        ChooseLang,
    },
    mounted() {
        this.user.lang = this.$refs.chooseLang.userLang;
    },
};
</script>
